import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import LineItemsGrid from "../../../../common/LineItemsGrid";
import "./DebitNoteView.css";
import { showMessageBox } from '../../../../site/fn';

// PDF Styles (same as Print.js)
const pdfStyles = StyleSheet.create({
  page: {
    fontSize: 10,
    padding: 10,
    paddingBottom: 40,
    color: "#000000",
  },
  viewer: {
    width: "100%",
    height: "90vh",
  },
  debitNoteTitle: {
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 2,
    padding: 6,
    backgroundColor: "#ffffff",
  },
  // ... (rest of styles from Print.js - abbreviated for brevity)
});

const DebitNoteView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debitNote, setDebitNote] = useState(null);
  const [company, setCompany] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const canEdit = hasPermission("ar-debit-note", "set");
  const canApprove = hasPermission("ar-debit-note", "approve");

  useEffect(() => {
    fetchDebitNote();
  }, [id]);

  const fetchDebitNote = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('zTaskAuthToken');
      
      // Fetch debit note data
      const data = await arService.getDebitNoteById(id);
      setDebitNote(data);
      
      // If approved, fetch additional data for PDF
      if (data.approval_status === "Approved") {
        const API_BASE = process.env.REACT_APP_NODE_SERVICE_BASE_URL || 'http://localhost:8081';
        
        // Fetch customer
        const customerResponse = await fetch(`${API_BASE}/customer/api/customers/${data.customer_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (customerResponse.ok) setCustomer(await customerResponse.json());
        
        // Fetch site if exists
        if (data.site_id) {
          const siteResponse = await fetch(`${API_BASE}/customer-site/api/customer-sites/${data.site_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (siteResponse.ok) setSite(await siteResponse.json());
        }
        
        // Fetch project if exists
        if (data.project_id) {
          const projectResponse = await fetch(`${API_BASE}/finance-helpers/api/projects/${data.project_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (projectResponse.ok) setProject(await projectResponse.json());
        }
        
        // Set company data
        setCompany({
          name: "Pune Logitech Services Pvt. Ltd.",
          address: "Gat No.299/3, Maan Road, Phase-3, Near Hinjewadi Metro Car Shed, Hinjewadi, Pune 411057",
          city: "Pune",
          state: "Maharashtra",
          phone: "+91-80-12345678",
          email: "raju@punelogitech.in",
          gstin: "27AAPCP0240H1ZG",
          pan: "AAPCP0240H"
        });
      }
    } catch (error) {
      console.error("Error fetching debit note:", error);
      showMessageBox("Failed to fetch debit note details.", "error");
      navigate("/ar/debit-notes");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (status, approvalStatus, isDraft) => {
    if (isDraft) {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (approvalStatus === "Pending") {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve Debit Note ${debitNote.doc_no}?`)) {
      return;
    }

    try {
      await arService.approveDebitNote(id);
      showMessageBox("Debit Note approved successfully!", "success");
      fetchDebitNote();
    } catch (error) {
      console.error("Error approving debit note:", error);
      const errorMessage = error.message || "Failed to approve debit note. Please try again.";
      showMessageBox(errorMessage, "error");
    }
  };

  const handleReject = async () => {
    const reason = window.prompt(`Enter reason for rejecting Debit Note ${debitNote.doc_no}:`);
    if (!reason) return;

    try {
      await arService.rejectDebitNote(id, reason);
      showMessageBox("Debit Note rejected successfully!", "success");
      fetchDebitNote();
    } catch (error) {
      console.error("Error rejecting debit note:", error);
      const errorMessage = error.message || "Failed to reject debit note. Please try again.";
      showMessageBox(errorMessage, "error");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading debit note...</p>
      </div>
    );
  }

  if (!debitNote) {
    return (
      <div className="error-container">
        <p>Debit note not found.</p>
        <NavLink to="/ar/debit-notes">
          <button className="btn-back">Back to Debit Notes</button>
        </NavLink>
      </div>
    );
  }

  // If approved, show PDF viewer
  if (debitNote.approval_status === "Approved") {
    // Redirect to print page for approved debit notes
    navigate(`/ar/debit-notes/${id}/print`, { replace: true });
    return null;
  }

  // Otherwise show form view with approval buttons
  return (
    <div className="debit-note-view-container">
      <div className="view-header no-print">
        <div className="header-left">
          <NavLink to="/ar/debit-notes">
            <button className="btn-back">← Back to List</button>
          </NavLink>
        </div>
        <div className="header-right">
          {canEdit && (debitNote.is_draft || debitNote.approval_status === "Rejected") && (
            <NavLink to={`/ar/debit-notes/${id}/edit`}>
              <button className="btn-edit">✏️ Edit</button>
            </NavLink>
          )}
          {canApprove &&
            (debitNote.approval_status === "Pending Approval" ||
              debitNote.approval_status === "Pending") && (
            <>
              <button className="btn-approve" onClick={handleApprove}>
                ✓ Approve
              </button>
              <button className="btn-reject" onClick={handleReject}>
                ✗ Reject
              </button>
            </>
          )}
        </div>
      </div>

      <div className="debit-note-document">
        {/* Document Header */}
        <div className="document-header">
          <h1>DEBIT NOTE</h1>
          <div className="doc-info">
            <div className="doc-number">
              <strong>Doc No:</strong> {debitNote.doc_no || "N/A"}
            </div>
            <div className="doc-status">{getStatusBadge(debitNote.status, debitNote.approval_status, debitNote.is_draft)}</div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="info-section">
          <h3>Customer & Project Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Debit Note Date:</label>
              <span>{formatDate(debitNote.debit_note_date)}</span>
            </div>
            <div className="info-item">
              <label>Customer:</label>
              <span>{debitNote.customer_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Project:</label>
              <span>{project?.project_name || debitNote.project_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>City:</label>
              <span>{debitNote.city || project?.city || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>State:</label>
              <span>{debitNote.state || project?.state_name || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Invoice & Order Details */}
        {(debitNote.original_invoice_no || debitNote.buyer_order_no) && (
          <div className="info-section">
            <h3>Invoice & Order Details</h3>
            <div className="info-grid">
              {debitNote.original_invoice_no && (
                <>
                  <div className="info-item">
                    <label>Original Invoice No:</label>
                    <span>{debitNote.original_invoice_no}</span>
                  </div>
                  <div className="info-item">
                    <label>Original Invoice Date:</label>
                    <span>{formatDate(debitNote.original_invoice_date)}</span>
                  </div>
                </>
              )}
              {debitNote.buyer_order_no && (
                <>
                  <div className="info-item">
                    <label>Buyer Order No:</label>
                    <span>{debitNote.buyer_order_no}</span>
                  </div>
                  <div className="info-item">
                    <label>Buyer Order Date:</label>
                    <span>{formatDate(debitNote.buyer_order_date)}</span>
                  </div>
                </>
              )}
              {debitNote.period_from && (
                <>
                  <div className="info-item">
                    <label>Period From:</label>
                    <span>{formatDate(debitNote.period_from)}</span>
                  </div>
                  <div className="info-item">
                    <label>Period To:</label>
                    <span>{formatDate(debitNote.period_to)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="info-section">
          <h3>Reason for Debit Note</h3>
          <p className="reason-text">{debitNote.reason || "N/A"}</p>
        </div>

        {/* Line Items */}
        {debitNote.line_items && debitNote.line_items.length > 0 && (
          <div className="info-section">
            <LineItemsGrid
              lineItems={debitNote.line_items}
              readOnly={true}
              variant="debit-note"
            />
          </div>
        )}

        {/* Amount Details */}
        <div className="info-section">
          <h3>Amount Details</h3>
          <div className="amount-table">
            <div className="amount-row">
              <span>Taxable Amount:</span>
              <strong>{formatCurrency(debitNote.taxable_amount)}</strong>
            </div>
            {parseFloat(debitNote.cgst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>CGST @ {debitNote.cgst_percentage || 0}%:</span>
                <strong>{formatCurrency(debitNote.cgst_amount)}</strong>
              </div>
            )}
            {parseFloat(debitNote.sgst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>SGST @ {debitNote.sgst_percentage || 0}%:</span>
                <strong>{formatCurrency(debitNote.sgst_amount)}</strong>
              </div>
            )}
            {parseFloat(debitNote.igst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>IGST @ {debitNote.igst_percentage || 0}%:</span>
                <strong>{formatCurrency(debitNote.igst_amount)}</strong>
              </div>
            )}
            <div className="amount-row subtotal">
              <span>Total Tax:</span>
              <strong>{formatCurrency(
                (parseFloat(debitNote.cgst_amount || 0) + 
                 parseFloat(debitNote.sgst_amount || 0) + 
                 parseFloat(debitNote.igst_amount || 0))
              )}</strong>
            </div>
            <div className="amount-row total">
              <span>Grand Total:</span>
              <strong>{formatCurrency(debitNote.total_amount)}</strong>
            </div>
            {debitNote.amount_in_words && (
              <div className="amount-in-words">
                <em>Amount in Words: {debitNote.amount_in_words}</em>
              </div>
            )}
          </div>
        </div>

        {/* Additional Details */}
        {(debitNote.terms_of_payment || debitNote.dispatch_doc_no || debitNote.remarks) && (
          <div className="info-section">
            <h3>Additional Details</h3>
            <div className="info-grid">
              {debitNote.terms_of_payment && (
                <div className="info-item full-width">
                  <label>Terms of Payment:</label>
                  <span>{debitNote.terms_of_payment}</span>
                </div>
              )}
              {debitNote.dispatch_doc_no && (
                <div className="info-item">
                  <label>Dispatch Document No:</label>
                  <span>{debitNote.dispatch_doc_no}</span>
                </div>
              )}
              {debitNote.dispatch_through && (
                <div className="info-item">
                  <label>Dispatch Through:</label>
                  <span>{debitNote.dispatch_through}</span>
                </div>
              )}
              {debitNote.destination && (
                <div className="info-item">
                  <label>Destination:</label>
                  <span>{debitNote.destination}</span>
                </div>
              )}
              {debitNote.terms_of_delivery && (
                <div className="info-item">
                  <label>Terms of Delivery:</label>
                  <span>{debitNote.terms_of_delivery}</span>
                </div>
              )}
              {debitNote.remarks && (
                <div className="info-item full-width">
                  <label>Remarks:</label>
                  <span>{debitNote.remarks}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebitNoteView;
